/**
 * API client for UI default configurations.
 * Fetches matching config from API and saves config (admin/dev).
 */

const apiBase = import.meta.env.VITE_API_BASE || 'https://api.pkmng.orly.io/api';

/**
 * Fetch the best matching UI config for the given screen dimensions and orientation.
 * @param {number} width - Screen width in pixels
 * @param {number} height - Screen height in pixels
 * @param {string} orientation - 'portrait' or 'landscape'
 * @param {string} [deviceName] - Optional device identifier
 * @returns {Promise<{ config: object|null }>} Response with config object or null
 */
export async function matchConfig(width, height, orientation, deviceName = null) {
    const params = new URLSearchParams({
        width: String(width),
        height: String(height),
        orientation,
    });
    if (deviceName) {
        params.set('device_name', deviceName);
    }
    const url = `${apiBase}/ui-configs/match/?${params.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`UI config match failed: ${response.status}`);
    }
    return response.json();
}

/**
 * Save (upsert) a UI config to the API. Requires admin or deployment token.
 * @param {string|null} deviceName - Optional device identifier
 * @param {number} width - Screen width in pixels
 * @param {number} height - Screen height in pixels
 * @param {string} orientation - 'portrait' or 'landscape'
 * @param {object} config - Full config object (e.g. portrait or landscape config)
 * @returns {Promise<object>} Created/updated config from API
 */
export async function saveConfig(deviceName, width, height, orientation, config) {
    const url = `${apiBase}/ui-configs/`;
    const body = {
        device_name: deviceName || null,
        screen_width: width,
        screen_height: height,
        orientation,
        config,
    };
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Save config failed: ${response.status}`);
    }
    return response.json();
}
